import { isNull } from "lodash";
import { useMemo, useEffect } from "react";
import { DeepPartial, FieldValues, UseFormReturn } from "react-hook-form";
import { Subject } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { AnyObjectSchema, mixed, ObjectSchema } from "yup";
import { ObjectShape } from "yup/lib/object";
import { LaunchConfigurationsDTO } from "../../../store/launch-configurations/models/dto";
import { deepOmitBy } from "../../../utils/utils";
import { FormType } from "./common/form";

/**
 * This method will call the callback everytime the form value changes,
 * debounced by 300ms
 * @param form The parent form
 * @param callback the callback that should update the parent form
 */
export function useDebouncedFormChange<T extends FieldValues>(
  form: UseFormReturn<T, any>,
  callback: (d: DeepPartial<T>) => void
) {
  const changeSubject = useMemo(() => new Subject<DeepPartial<T>>(), []);

  useEffect(() => {
    const sub = changeSubject.pipe(debounceTime(300)).subscribe(callback);
    return () => sub.unsubscribe();
  }, [changeSubject, callback]);

  useEffect(() => {
    const sub = form.watch((x) => changeSubject.next(x));
    return () => sub.unsubscribe();
  }, [form.watch, changeSubject]);
}

/**
 * Will deeply remove all the null in the object
 * @param item the object
 * @returns new object
 */
export function useRemoveNulls(item: any) {
  return useMemo(() => item && deepOmitBy(item, isNull), [item]);
}

export function useParentFormSchema(): AnyObjectSchema {
  const schema = LaunchConfigurationsDTO.Requests.SetSingle.useSchema();

  return useMemo(
    () =>
      schema.shape({
        headerImage: mixed()
          .test(
            "is-large",
            "File cannot exceed 6MB",
            (file: any) =>
              !(file instanceof File) || file.size <= 6 * 1024 * 1024
          )
          .label("file"),
      }),
    [schema]
  );
}
